import React, { useState, useMemo, useEffect } from 'react';
import { IconButton, Tooltip, TextField, InputAdornment, ListSubheader } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import debounce from 'lodash.debounce';
import CreatePageConstants from '../../CreatePageConstants';
import { FONT_SOURCE } from '../../Preview/PreviewDisplayConstants';
import SelectListItem from '../../../../list-items/SelectListItem/SelectListItem';
import { loadGoogleFont } from '../../../../../utilities/fontUtilities';

const fontOptions = CreatePageConstants.FONT_OPTIONS;

const FontSelectListItem = ({ value, onChange, searchGoogleFonts, text = 'Font' }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);

    const debouncedSearch = useMemo(
        () => debounce(async (query) => {
            if (!query.trim()) {
                setSearchResults([]);
                return;
            }
            try {
                const response = await searchGoogleFonts(query, 0, 10);
                const fonts = response.data || [];
                setSearchResults(fonts);
                fonts.forEach(f => loadGoogleFont(f.family));
            } catch (error) {
                console.error('Failed to search fonts', error);
            }
        }, 500),
        [searchGoogleFonts]
    );

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    const onSearchChange = (e) => {
        setSearchTerm(e.target.value);
        debouncedSearch(e.target.value);
    };

    const handleFontChange = (event) => {
        const selectedFontValue = event.target.value;
        if (selectedFontValue === 'SEARCH_INPUT' || selectedFontValue === 'NO_RESULTS') return;

        const isSystemFont = fontOptions.some(o => o.value === selectedFontValue);
        const source = isSystemFont ? FONT_SOURCE.SYSTEM : FONT_SOURCE.GOOGLE_FONTS;

        onChange(selectedFontValue, source);
    };

    const options = useMemo(() => {
        const isSearchActive = searchTerm.trim().length > 0;
        
        // Return search results directly when searching
        if (isSearchActive) {
            let searchResultOptions;
            if (searchResults.length === 0) {
                searchResultOptions = [{ value: 'NO_RESULTS', text: 'No results found', font: 'inherit', disabled: true, style: { justifyContent: 'center' } }];
            } else {
                searchResultOptions = searchResults.map(f => ({ value: f.family, text: f.family, font: f.family }));
            }
            
            // Ensure the currently selected value is in the options so MUI Select doesn't go blank
            const valueInResults = searchResultOptions.some(o => o.value === value);
            if (!valueInResults && value) {
                const currentOption = fontOptions.find(o => o.value === value) 
                    || { value: value, text: value, font: value };
                
                return [
                    { ...currentOption, style: { ...currentOption.style, display: 'none' } },
                    ...searchResultOptions
                ];
            }
            return searchResultOptions;
        }

        // Return default options if no specific value is selected
        if (!value) {
            return [...fontOptions];
        }

        // When a value is selected and no search is active, ensure the selected font is at the top
        const defaultOptionsWithoutSelected = fontOptions.filter(o => o.value !== value);
        const selectedOption = fontOptions.find(o => o.value === value) 
            || { value: value, text: value, font: value };

        return [selectedOption, ...defaultOptionsWithoutSelected];
    }, [value, searchResults, searchTerm]);

    return (
        <SelectListItem
            text={text}
            value={value}
            onChange={handleFontChange}
            options={options}
            MenuProps={{ autoFocus: false }}
            actionIconButton={
                <Tooltip title="Browse Google Fonts" arrow placement="top">
                    <IconButton
                        size="small"
                        onClick={() => window.open('https://fonts.google.com/', '_blank')}
                        sx={{ ml: 0.5 }}
                    >
                        <OpenInNewIcon sx={{ color: '#ffffffb3' }} fontSize="small" />
                    </IconButton>
                </Tooltip>
            }
        >
            <ListSubheader disableSticky onClick={(e) => e.stopPropagation()}>
                <TextField
                    size="small"
                    autoFocus
                    placeholder="Search Google Fonts"
                    fullWidth
                    value={searchTerm}
                    onChange={onSearchChange}
                    onKeyDown={(e) => {
                        if (e.key !== 'Tab') {
                            e.stopPropagation();
                        }
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ my: 1 }}
                />
            </ListSubheader>
        </SelectListItem>
    );
};

export default FontSelectListItem;
